import {
    AnalyticsUserEvent,
    MoneyMapAccountStatus,
    MoneyMapAuthorType,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import { Button, ButtonVariant, FrostedSurface, YStack } from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { SIDEBAR_CONTENT_MAX_WIDTH } from '../../../common/utils/homeUtils';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { useGetMembershipMastraThread } from '../../../llm-service/hooks/useGetMembershipMastraThread';
import { track } from '../../../logger/Logger';
import { useMoneyMapWorkflow } from '../../../roadmap/hooks/useMoneyMapWorkflow';
import { userFirstNameAtom } from '../../../user/state/UserAtoms';
import {
    MoneyMapProjectionGraphic,
    useMoneyMapProjectionDisplay,
} from './MoneyMapProjectionGraphic';

/**
 * Standalone home card that surfaces the money map projection graph (the same
 * headline + timeline graph shown in HomeGoalTile).
 *
 * Step 1: renders the repurposed graph with a visual-only "Activate your money
 * system" button. Session-booked gating and the button's destination are
 * follow-up pieces.
 */
export const MoneyMapProjectionCard = ({
    backgroundColor,
}: {
    /**
     * Solid fill for the desktop column. The mobile hero leaves this unset and
     * uses the frosted surface over the home gradient.
     */
    backgroundColor?: '$gray200';
}) => {
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const user = useAuthedUser();
    const membershipId = user.membership?.id;

    const { data: results } = useGetMembershipMastraThread({ membershipId });
    const display = useMoneyMapProjectionDisplay(results);
    const userFirstName = useAtomValue(userFirstNameAtom);

    const { isWorkflowCompleted, isLoadingCompleteness, moneyMap } =
        useMoneyMapWorkflow();

    // A guide-authored money map (e.g. a guide duplicated/edited a CMM map)
    // exits the GOYO/robo experience, so fall back to the classic money-map CTA
    // (handled by HomeMoneyMapSection) rather than this GOYO projection card.
    const isGuideAuthoredMoneyMap =
        moneyMap?.createdByType === MoneyMapAuthorType.GUIDE;

    // The member hasn't opened their Fruitful accounts yet while their cash
    // accounts step is still incomplete (cash is the foundational account —
    // card/investment opening comes after it, so this is a reliable "opened no
    // accounts" signal). CMM maps are born ACTIVE with every money map account
    // still PROPOSED, so this is the common post-onboarding state.
    const hasNotOpenedAccounts = moneyMap?.moneyMapRules?.every(
        (rule) =>
            rule.moneyMapAccount.status === MoneyMapAccountStatus.PROPOSED,
    );

    // This "Activate your money system" card is the pre-activation state. Once
    // the member has activated their money system, it hands off to the normal
    // money-map / "Put your Money Map into action" widgets.
    //
    // We key off the GO workflow completion rather than the money map's own
    // `status === ACTIVE`: CMM onboarding creates the map as ACTIVE immediately
    // on signup (accounts still PROPOSED, no direct deposit), so `status` would
    // suppress this card for every CMM member from the moment they finish
    // onboarding. `isWorkflowCompleted` is false until the member actually
    // completes the GO steps, and it mirrors the handoff gate used by the
    // "Put your Money Map into action" (BankingSwitch) widget.
    const hasActivatedMoneyMap = isWorkflowCompleted;

    const handleActivatePress = () => {
        track(AnalyticsUserEvent.ONBOARDING_GOYO_PRESS_ACTIVATE_MONEY_SYSTEM);
        // If the member hasn't opened any accounts yet, drop them into the
        // onboarding review-numbers screen to review their Money Map before
        // opening accounts. Otherwise send them into the GO flow start screen
        // (mirrors the Money System tab's "Set up your Money Map" button).
        if (hasNotOpenedAccounts) {
            router.navigate('/onboarding/review-numbers');
            return;
        }
        router.navigate('/roadmap/go/start');
    };

    // Wait for the GO completeness to load before deciding. The workflow hook
    // seeds an all-incomplete `initialMoneyMapCompleteness` while `/completeness`
    // is in flight, so `isWorkflowCompleted` is briefly false even for members
    // who have finished GOYO — which would flash this card on screen and then
    // hide it once the real completeness arrives.
    if (
        !goyoEnabled ||
        isGuideAuthoredMoneyMap ||
        isLoadingCompleteness ||
        display == null ||
        hasActivatedMoneyMap
    ) {
        return null;
    }

    // In system-preview mode (no debt-free date) the Figma titles the card
    // "{first name}'s Money System" rather than the onboarding-chat fallback
    // headline. Timeline mode keeps its "Debt free by …" headline.
    const cardDisplay = display.showSystemPreview
        ? {
              ...display,
              headline:
                  userFirstName != null
                      ? `${userFirstName}\u2019s Money System`
                      : 'Your Money System',
          }
        : display;

    const cardContent = (
        <>
            <MoneyMapProjectionGraphic display={cardDisplay} graphOnly />
            <Button
                text="Activate your money system"
                variant={ButtonVariant.PRIMARY}
                width="full"
                onPress={handleActivatePress}
            />
        </>
    );

    return (
        <YStack
            width="100%"
            maxWidth={SIDEBAR_CONTENT_MAX_WIDTH}
            alignSelf="center"
            $md={{ maxWidth: '100%', alignSelf: 'stretch' }}
        >
            {backgroundColor != null ? (
                <YStack
                    width="100%"
                    backgroundColor={backgroundColor}
                    borderRadius={24}
                    overflow="hidden"
                    padding={16}
                    gap={16}
                >
                    {cardContent}
                </YStack>
            ) : (
                <FrostedSurface width="100%">
                    <YStack padding={16} gap={16}>
                        {cardContent}
                    </YStack>
                </FrostedSurface>
            )}
        </YStack>
    );
};

export default MoneyMapProjectionCard;
